import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { keyBy } from 'lodash-es'
import {
  CameraIcon,
  ClipboardListIcon,
  ExternalLinkIcon,
  HistoryIcon,
  MapPinIcon,
  WrenchIcon,
  type LucideIcon,
} from 'lucide-react'

import { useDexieQuery } from '@/hooks/use-dexie-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { ImagesSection } from '@/features/interventi/components/intervento-form/images-section'
import { InterventoStatoBadge } from '@/features/interventi/components/stato-badge'
import { foldOf } from '@/features/interventi/eventi'
import {
  repairPhotos,
  repairTitles,
  StoricoTimeline,
} from '@/features/riparazioni/components/storico-timeline'
import { useOperatoreNames } from '@/features/utenti/use-operatore-names'
import { interventoSearchSchema } from '@/routes/_authenticated/interventi/-intervento-search-schema'

export const Route = createFileRoute(
  '/_authenticated/interventi/visualizza/$interventoId'
)({
  component: RouteComponent,
  ssr: false,
  validateSearch: interventoSearchSchema,
})

function SectionHeader({
  title,
  icon: Icon,
}: {
  title: string
  icon: LucideIcon
}) {
  return (
    <CardHeader className="px-4 pt-2.5 pb-1.5">
      <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900 sm:text-xl">
        <Icon className="size-5 text-gray-500" />
        {title}
      </CardTitle>
    </CardHeader>
  )
}

function SummaryField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium tracking-[0.16em] text-gray-500 uppercase">
        {label}
      </p>
      <div>{children}</div>
    </div>
  )
}

function TextValue({ children }: { children: React.ReactNode }) {
  return <p className="text-base font-semibold text-gray-900">{children}</p>
}

function NoteBlock({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium tracking-[0.16em] text-gray-500 uppercase">
        {label}
      </p>
      <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3">
        <p className="leading-relaxed whitespace-pre-wrap text-gray-900">
          {value?.trim() ? value : '-'}
        </p>
      </div>
    </div>
  )
}

function getGoogleMapsLink(coordinate?: string | null) {
  if (!coordinate) return undefined

  const [lat, lng] = coordinate.split(',').map((value) => value.trim())
  if (!lat || !lng) return undefined

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`
}

function RouteComponent() {
  const { interventoId } = Route.useParams()

  const intervento = useDexieQuery((db) => db.interventi.get(interventoId))

  const fotoInterventoList = useDexieQuery((db) =>
    db.foto_interventi
      .where({ intervento_id: interventoId })
      .filter((f) => !f.deleted_at)
      .toArray()
  )

  const names = useOperatoreNames()
  const repairFotos = useMemo(
    () =>
      intervento ? repairPhotos(intervento, fotoInterventoList ?? []) : [],
    [intervento, fotoInterventoList]
  )

  if (!intervento) return null

  // The inspection's photos in their slots, a repair's after them and in the
  // Storico: one carousel, Immagini's, for all of them
  const fotoInterventoByCategory = keyBy(
    fotoInterventoList?.filter((f) => f.riparazione_id == null),
    'categoria'
  )
  // The notes of the repairs closed, named as in the Storico
  const { phases } = foldOf(intervento)
  const titles = repairTitles(phases)
  const repairNotes = phases.flatMap((phase) =>
    phase.kind === 'repair' && phase.closedAt && !phase.cancelled && phase.note
      ? [
          {
            id: phase.repairId!,
            title: titles.get(phase.repairId!)!,
            note: phase.note,
          },
        ]
      : []
  )
  const formValues = {
    fotoIntervento: fotoInterventoByCategory,
  }
  const googleMapsLink = getGoogleMapsLink(intervento.coordinate)

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Interventi', to: '/interventi' },
          { name: `Intervento ${intervento.id_interno}` },
        ]}
      />

      {/* Wide: the intervento on the left, its Storico beside it; narrow: one
          column, the Storico after the Riepilogo */}
      <div className="mx-auto mt-6 grid max-w-6xl grid-cols-1 items-start gap-8 lg:grid-cols-3">
        <Card className="gap-2 border-gray-200 bg-white py-2 shadow-none lg:col-span-2">
          <SectionHeader
            title={`Riepilogo intervento ${intervento.id_interno}`}
            icon={ClipboardListIcon}
          />
          <CardContent className="grid gap-6 border-t border-gray-100 pt-3.5 sm:grid-cols-2">
            <SummaryField label="Stato">
              <InterventoStatoBadge
                intervento={intervento}
                className="rounded-md px-3 py-1 text-sm font-semibold sm:text-base"
              />
            </SummaryField>

            <SummaryField label="ID interno">
              <TextValue>{intervento.id_interno}</TextValue>
            </SummaryField>

            <SummaryField label="Tipologia caditoia">
              <p className="text-base font-semibold text-gray-900 capitalize">
                {intervento.tipologia_caditoia?.replace('_', ' ')}
              </p>
            </SummaryField>

            <SummaryField label="Numero caditoie">
              <TextValue>{intervento.caditoie_num}</TextValue>
            </SummaryField>
            {/* When it started and ended: the Storico's, beside it */}
          </CardContent>
        </Card>

        <Card className="gap-2 border-gray-200 bg-white py-2 shadow-none lg:sticky lg:top-4 lg:col-start-3 lg:row-span-3 lg:row-start-1">
          <SectionHeader title="Storico" icon={HistoryIcon} />
          <CardContent className="border-t border-gray-100 pt-3.5">
            <StoricoTimeline
              intervento={intervento}
              fotos={fotoInterventoList}
              names={names}
              ownCarousel={false}
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:col-span-2">
          {(intervento.indirizzo || intervento.coordinate) && (
            <Card className="gap-2 border-gray-200 bg-white py-2 shadow-none">
              <SectionHeader title="Ubicazione" icon={MapPinIcon} />
              <CardContent className="space-y-6 border-t border-gray-100 pt-3.5">
                {intervento.indirizzo && (
                  <SummaryField label="Indirizzo">
                    <TextValue>{intervento.indirizzo}</TextValue>
                  </SummaryField>
                )}

                {intervento.coordinate && (
                  <SummaryField label="Coordinate">
                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3">
                      <div className="space-y-3">
                        <div className="overflow-x-auto">
                          <p className="font-mono text-sm font-medium text-gray-900 sm:text-base">
                            <span className="sm:hidden">
                              {intervento.coordinate
                                .split(',')
                                .map((part) => part.trim())
                                .map((part, index, parts) => (
                                  <span key={`${part}-${index}`}>
                                    {part}
                                    {index < parts.length - 1 && <br />}
                                  </span>
                                ))}
                            </span>
                            <span className="hidden whitespace-nowrap sm:inline">
                              {intervento.coordinate}
                            </span>
                          </p>
                        </div>
                        {googleMapsLink && (
                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              size="icon"
                              className="bg-white"
                              asChild
                            >
                              <a
                                href={googleMapsLink}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Apri in Google Maps"
                                title="Apri in Google Maps"
                              >
                                <ExternalLinkIcon />
                              </a>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </SummaryField>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="gap-2 border-gray-200 bg-white py-2 shadow-none">
            <SectionHeader title="Note intervento" icon={WrenchIcon} />
            <CardContent className="space-y-6 border-t border-gray-100 pt-3.5">
              <NoteBlock label="Note pre" value={intervento.note_pre} />
              <NoteBlock label="Note post" value={intervento.note_post} />
              {repairNotes.map(({ id, title, note }) => (
                <NoteBlock
                  key={id}
                  label={`Note ${title.toLowerCase()}`}
                  value={note}
                />
              ))}
            </CardContent>
          </Card>
        </div>

        <section className="space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2">
            <CameraIcon className="size-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
              Immagini
            </h2>
          </div>

          <ImagesSection
            form={{} as any}
            formValues={formValues}
            readonly={true}
            morePhotos={repairFotos}
          />
        </section>
      </div>
    </>
  )
}
